/**
 * Maaş raporu — CSV indir.
 * Excel/Numbers/Sheets hepsi açar. UTF-8 BOM ile Türkçe karakterler doğru görünür.
 */

import type { ToplamMaliyetOzeti } from './toplam-maliyet'
import type { MaasOdemesi } from '@/lib/services/maas-odeme-service'
import type { Sube, Pozisyon } from '@/types'

function csvEscape(v: string | number | undefined | null): string {
  if (v === undefined || v === null) return ''
  const s = String(v)
  if (/[",\n;]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

function fmt(n: number): string {
  return n.toFixed(2).replace('.', ',')
}

function ayEtiketi(ay: string): string {
  const [y, m] = ay.split('-').map(Number)
  if (!y || !m) return ay
  const aylar = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']
  return `${aylar[m - 1]} ${y}`
}

export function maaslariCsvOlustur(params: {
  donem: string
  ozet: ToplamMaliyetOzeti
  odemeler: MaasOdemesi[]
  subeler: Sube[]
  pozisyonlar: Pozisyon[]
  isBuAy: boolean
}): string {
  const { donem, ozet, odemeler, subeler, pozisyonlar, isBuAy } = params

  const subeMap = new Map(subeler.map((s) => [s.id, s.isim]))
  const pozMap = new Map(pozisyonlar.map((p) => [p.id, p.isim]))

  const odemeMap = new Map<string, MaasOdemesi[]>()
  odemeler
    .filter((o) => !o.iptal)
    .sort((a, b) => a.odemeTarihi.toMillis() - b.odemeTarihi.toMillis())
    .forEach((o) => {
      const list = odemeMap.get(o.personelId) ?? []
      list.push(o)
      odemeMap.set(o.personelId, list)
    })

  const lines: string[] = []
  lines.push(`Maaş Raporu;${ayEtiketi(donem)}`)
  lines.push(`Oluşturma;${new Date().toLocaleString('tr-TR')}`)
  lines.push('')

  const headers = [
    'Ad Soyad', 'IBAN', 'Şubeler', 'Pozisyonlar', 'Maaş Tipi',
    'Çalışma Saati', 'Saatlik Karşılık (₺)', 'Baz Ücret (₺)',
    'Mola Aşımı (₺)', 'Puantaj (₺)', 'Avans (₺)',
    'Sabit Ek Ödeme (₺)', 'Sabit Kesinti (₺)',
    isBuAy ? 'Anlık Net (₺)' : 'Net Maaş (₺)',
    'Toplam Ödenen (₺)', 'Kalan/Fazla (₺)',
    'Ödeme Durumu', 'Ödeme Detayı',
  ]
  lines.push(headers.map(csvEscape).join(';'))

  let toplamNet = 0
  let toplamOdenen = 0

  for (const { personel, hesap } of ozet.satirlar) {
    const subeAdlari = personel.subeIdleri.map((id) => subeMap.get(id) ?? '?').join(', ')
    const pozAdlari = personel.pozisyonIdleri.map((id) => pozMap.get(id) ?? '?').join(', ')
    const odenecek = isBuAy ? hesap.anlikNet : hesap.netMaas
    const odList = odemeMap.get(personel.id) ?? []
    const odenenTop = odList.reduce((acc, o) => acc + o.netTutar, 0)
    const fark = odenecek - odenenTop

    let durum = 'Bekliyor'
    if (odenenTop > 0) {
      if (Math.abs(fark) <= 1) durum = 'Tamamı Ödendi'
      else if (fark > 1) durum = 'Kısmi Ödendi'
      else durum = 'Fazla Ödeme'
    }

    const odemeDetay = odList
      .map((o) => {
        if (o.odemeTipi === 'nakit') return `Nakit ${fmt(o.netTutar)}`
        if (o.odemeTipi === 'iban') return `IBAN ${fmt(o.netTutar)}`
        return `Karma N${fmt(o.nakitTutar ?? 0)}+I${fmt(o.ibanTutar ?? 0)}`
      })
      .join(' | ')

    const row = [
      personel.isim, personel.ibanNo ?? '', subeAdlari, pozAdlari,
      personel.maasTipi === 'aylik' ? 'Aylık' : 'Saatlik',
      hesap.toplamSaat.toFixed(2).replace('.', ','),
      fmt(hesap.saatlikKarsilik),
      fmt(isBuAy ? hesap.anlikBaz : hesap.bazUcret),
      fmt(hesap.molaAsanKesinti),
      fmt(hesap.puantajToplam),
      fmt(hesap.odenmisAvansToplam),
      fmt(hesap.sabitEkOdeme),
      fmt(hesap.sabitKesinti),
      fmt(odenecek),
      fmt(odenenTop),
      fmt(fark),
      durum,
      odemeDetay,
    ]
    lines.push(row.map(csvEscape).join(';'))
    toplamNet += odenecek
    toplamOdenen += odenenTop
  }

  lines.push('')
  lines.push(`Toplam Net;;;;;;;;;;;;;${fmt(toplamNet)};${fmt(toplamOdenen)};${fmt(toplamNet - toplamOdenen)};;`)

  return lines.join('\n')
}

export function csvIndir(icerik: string, dosyaAdi: string) {
  const blob = new Blob(['\uFEFF' + icerik], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = dosyaAdi
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
